import { z } from 'zod';

/**
 * Canonical customer-identity vocabulary — SINGLE SOURCE OF TRUTH.
 *
 * A `customerId` is an identity-service `User.id` or it is nothing. It is never
 * `'kiosk'`, never `KIOSK_WALKUP_<id>`, never `service:<type>`, never an
 * all-zeros UUID, never an email address. Anything that validates, transmits or
 * authorises against a customer identifier MUST derive from this declaration
 * rather than re-declare it (AGENTS.md rules #1 and #7c).
 *
 * The rule and the reasoning are
 * [ADR-037](../../../docs/design/adr/ADR-037-customer-identity-vocabulary.md);
 * epic #841 implements it.
 *
 * ## What a `User.id` actually is
 *
 * `services/identity-service/prisma/schema.prisma:26` is
 * `id String @id @default(cuid())`. On Prisma 5.9 that emits a **cuid v1**: 25
 * characters, lowercase base36, always leading `c` — `clx3f9k2a0000v8h1abcd2efg`.
 *
 * It is **not a UUID**, and that mistake is the origin of this epic.
 * `docs/database-schema.md` specified `customer_id UUID NOT NULL`, and the wrong
 * type propagated into four *runtime* validators that reject real customer ids
 * today (`event-schemas/src/ticketing.ts:246`, `event-schemas/src/analytics.ts:53`,
 * `api-contracts/src/ticketing/index.ts:2305`, and
 * `analytics-service/prisma/schema.prisma:23`, a `@db.Uuid` column that can never
 * hold one). `.uuid()` is prohibited on any customer identifier. Corrections are
 * tracked by #858 and #860.
 *
 * **This repository mints two different cuid formats side by side.** Prisma
 * generates v1 as above, while `payment-service/src/payments/payments.service.ts`
 * and `capacity-service/src/attraction-slots/attraction-slots.service.ts` call
 * `createId()` from `@paralleldrive/cuid2`, which produces **24** characters with
 * an arbitrary leading letter (`dhxviibg2nahldkvg3g06u4a`). Those are
 * `Payment.id` and slot ids, never user ids, so the pattern below is correct.
 *
 * The hazard is a schema change, not a version bump. `@default(cuid())` still
 * emits v1 on the installed Prisma 5.22; `cuid(2)` is an opt-in change to
 * `schema.prisma`, so upgrading Prisma alone breaks nothing. What would break it
 * is someone writing `@default(cuid(2))` on `User.id` — and
 * `api-contracts/src/__tests__/customer-id-vocabulary.spec.ts` reads that schema
 * off disk and fails if they do, which is the actual guard. Nothing else would
 * catch it: the column type does not change and all sixteen services build with
 * swc and `typeCheck: false`, so it would surface as authorisation denials.
 *
 * ## Why this lives in shared-types, and what it cost
 *
 * `@autopia/event-schemas` and `@autopia/api-contracts` each depend on
 * shared-types *and* zod, but not on each other, so there is no other package
 * both zod consumers can import a schema from. Declaring it here is the
 * resolution, and it means `@autopia/shared-types` stops being a
 * zero-dependency, type-only package. That trade was taken deliberately and is
 * recorded in ADR-037 §4 rather than slipped in.
 *
 * **It is not free, and an earlier wording of this comment said it was.** Four
 * workspaces depend on shared-types and have neither zod nor api-contracts —
 * `@autopia/auth-guard`, `@autopia/kafka-client`, `@autopia/saga` and
 * `@autopia/testing` — so for them this is a genuinely new transitive
 * dependency, and `auth-guard` is on every service's JWT path. The load is
 * eager: `dist/index.js` re-exports this module, which `require`s zod.
 *
 * Mitigated rather than dismissed: `package.json` exposes a `./customer-id`
 * subpath so a zod-free consumer can import the schema without dragging the
 * barrel, and `sideEffects: false` lets a bundler drop it when unused. The cost
 * where it is genuinely paid — one zod copy, already deduplicated by
 * `node-linker=hoisted` — is small, but it should be stated rather than argued
 * away.
 *
 * A branded nominal type was considered and rejected; see ADR-037 Alternative 5.
 * All sixteen services build with `"builder": "swc"` and `"typeCheck": false`, so
 * a brand violation compiles green and surfaces only under a separate
 * `turbo typecheck` — and zod's own documentation is explicit that
 * *"branded types do not affect the runtime result of `.parse`. It is a
 * static-only construct."* Runtime validation is what catches a sentinel arriving
 * over HTTP or Kafka from a service that has not been redeployed. A brand cannot.
 */

/**
 * An identity-service `User.id`.
 *
 * One positive check, not a list of forbidden values. That is deliberate: the
 * previous guard (`payment-service/src/payments/payment-access.ts`) was a
 * deny-list, and it went stale in both directions at once — it missed two live
 * sentinels and carried a dead branch for a sixth that no longer existed. Six
 * shapes were invented by four teams and nobody updated the list.
 *
 * OWASP's Input Validation Cheat Sheet is explicit that this is the wrong shape:
 * *"Allowlist validation involves defining exactly what IS authorized, and by
 * definition, everything else is not authorized"*, whereas denylisting is *"a
 * massively flawed approach"* that *"should not be relied upon as the primary
 * method"*.
 *
 * Describing what a valid id looks like rejects every sentinel in
 * {@link SENTINEL_CUSTOMER_ID} *and* emails, real UUIDs, cuid2s, the empty
 * string, and whatever shape gets invented next — with no list for anyone to
 * remember to extend.
 */

/**
 * The shape of an identity-service `User.id`, as a bare pattern.
 *
 * cuid v1 — 25 characters, lowercase base36, always a leading `c`. Prisma's
 * `@default(cuid())` on 5.9+. Not a UUID, and not a cuid2 either: `@paralleldrive/cuid2`
 * produces 24 characters and is what mints `Payment.id`.
 *
 * {@link CustomerIdSchema} is the way to validate a value in TypeScript — reach for
 * this only where a zod schema cannot go. Two such places exist, and they are why
 * this is exported rather than inlined:
 *
 *   1. **#855's shape `CHECK`.** Postgres needs the literal, and it must be the same
 *      literal, or a row the application accepts is rejected by the constraint (or
 *      worse, the reverse). `^c[a-z0-9]{24}$` means the same thing to Postgres ARE
 *      as to JavaScript — no lookaround, no shorthand class, no flags.
 *   2. **#852's backfill script**, which classifies rows outside Nest's DI.
 *
 * ⚠️ **A pattern is not a validator.** Passing this says a value is *shaped* like a
 * `User.id`; it does not say identity-service has one. The backfill asserts existence
 * separately, and so should anything else that mints rather than reads.
 */
export const CUSTOMER_ID_PATTERN = /^c[a-z0-9]{24}$/;

export const CustomerIdSchema = z
  .string()
  .regex(CUSTOMER_ID_PATTERN, 'customerId must be an identity-service User.id');

/** A validated identity-service `User.id`. Derived — never re-declared (Rule #2). */
export type CustomerId = z.infer<typeof CustomerIdSchema>;

/**
 * Values that were written into customer-id fields and are not identities.
 *
 * **This is a classifier, not a validator.** It answers "is this row one of the
 * known bad shapes?", which is what #852's backfill and the shape census need in
 * order to *find* rows to fix. It
 * must never be used to decide whether a value is acceptable — that is
 * {@link CustomerIdSchema}'s job, and a value passing this pattern is not
 * thereby valid. OWASP permits a denylist only as *"an additional layer of
 * defense"*, never as the primary check.
 *
 * Declared here so the five shapes are written down once. A second copy lived in
 * `payment-service`'s `payment-access.ts` until #854 deleted it along with the
 * projection read it screened; this is now the only declaration.
 *
 * | Shape | Written by |
 * |---|---|
 * | `kiosk` | retired by #844; historic rows only |
 * | `service:<type>` | `payment-service/src/payments/payments.service.ts` — descoped, excluded structurally by `Payment.kind` |
 * | `WB_<code>` | retired by #835; historic rows only |
 * | `KIOSK_WALKUP_<kioskId>` | retired by #847/#848; survives only as ticketing's deploy-order fallback, removed at #856. See {@link kioskWalkupCustomerId} |
 * | all-zeros UUID | retired by #849; historic rows only |
 *
 * ⚠️ **`guest:<email>` is a sixth shape and is deliberately NOT in this pattern.**
 * Nothing mints it any more (see the #855 note below), but it reaches `Order.customerId`,
 * `Wristband.customerId` and, through `ORDER_CREATED`, the payment-service `orders`
 * projection. It matches neither this classifier nor {@link CustomerIdSchema}, so
 * `paymentOwnerOrNull` already leaves the payment `NULL`-owned, which ADR-037
 * decision 3 says is correct for a guest.
 *
 * It is excluded because this classifier's job is to find rows the backfill can
 * *resolve*, and a guest has no identity to resolve to. Adding it here would make
 * #852 look at rows it can only ever leave alone — and would change #852's
 * behaviour, which is why #870 left it out rather than tidying it in. The
 * consequence is that #853's census must bucket by {@link CustomerIdSchema}
 * pass/fail first and enumerate the failures it does not recognise — counting only
 * the five shapes above silently undercounts.
 *
 * ⚠️ **Updated 2026-08-13 by #870, which narrowed this but did not close it.** The
 * mint site used to be `req.user?.userId` falling back to `guest:` plus the
 * coordinator's email, on a route that permitted anonymous callers. That route now
 * requires authentication and validates the result against {@link CustomerIdSchema},
 * so the customer web path can no longer produce this shape at all.
 *
 * One producer remained after #870, and it was not a fallback: reseller-service
 * creates group bookings through the same endpoint using `X-Service-Secret` and no
 * bearer token, so it was admitted as `service:reseller-service` and minted
 * `guest:<email>` deliberately — writing the synthetic principal into the column
 * instead would have been a *new* sentinel, collapsing every partner's bookings onto
 * one identity.
 *
 * ⚠️ **Closed 2026-08-13 by #855. There is now no producer of this shape.**
 * `resolveCoordinatorId` refuses a SERVICE principal with 422
 * `COORDINATOR_IDENTITY_NOT_PROVISIONED` rather than minting, because a group booking
 * is `kind = 'ORDER'` and the presence `CHECK` #855 added to `payments` rejects an
 * unowned one. The cost is that partner group bookings fail until **#931** decides
 * which owner they carry; that is the same product decision, now forced rather than
 * deferred.
 *
 * **So this population no longer grows — but it does not drain either.** Rows written
 * before #855 still hold the shape, and nothing backfills them: a guest has no
 * identity to resolve to, which is the same reason this classifier excludes it. Any
 * "census reaches zero" gate must therefore be met by repairing or retiring those
 * rows, not by waiting. Tracked on **#931**.
 */
export const SENTINEL_CUSTOMER_ID =
  /^(kiosk$|service:|WB_|KIOSK_WALKUP_|0{8}-0{4}-0{4}-0{4}-0{12}$)/;

/**
 * Rebuilds the retired walk-up sentinel. **Read paths only, and transitional.**
 *
 * No producer emits this value after #847 and #848 — a walk-up order and the
 * wristband issued for it now carry the kiosk terminal's real `User.id`. But
 * rows written before that cutover still hold it, so the admin readers that
 * report on a kiosk's sales have to match *both* shapes until #852 backfills
 * them. This exists so that "both shapes" is spelled out in one place rather
 * than re-derived at each reader.
 *
 * It is a **constructor for a value {@link CustomerIdSchema} rejects**, which
 * reads oddly in this file. The alternative is worse: the formula existed at six
 * sites before this wave and had already drifted — `kiosk-purchase.service.ts`
 * truncated to 50 characters and `walk-up.service.ts` did not, under a comment
 * claiming the two mirrored each other. One declaration is what AGENTS.md rule
 * #7c asks for, and it is what makes the contract phase a single deletion
 * instead of a hand-search across three services and an app. Parallel Change's
 * documented failure is the transition nobody finishes.
 *
 * The `.slice(0, 50)` reproduces `kiosk-purchase.service.ts`'s historic write
 * **exactly**, which is the only thing a read key has to do. It is *not*
 * justified by "the column would have truncated it": Postgres `varchar(50)`
 * raises SQLSTATE 22001 on an over-long insert rather than truncating, so a
 * value that needed slicing would have failed at write and the row would not
 * exist. Keep the slice for parity with the writer, not for a database
 * behaviour that does not happen. (Moot in practice — `Kiosk.id` is a 25-char
 * cuid, so the built value is 38 characters and never reached the cap.)
 *
 * ⚠️ #886 widened `Order.customerId` from `VARCHAR(50)` to `TEXT`, so the cap
 * this slice was named after no longer exists. **The slice still stays.** It
 * has to reproduce what `kiosk-purchase.service.ts` wrote *at the time*, and
 * those rows are exactly the ones this key exists to find. Removing it would
 * silently stop matching any pre-cutover row whose built value exceeded 50
 * characters. It goes when the read paths do, not when the column changes.
 *
 * Deleted by #852 (backfill) / #857 (admin surfaces).
 */
export const kioskWalkupCustomerId = (kioskId: string): string =>
  `KIOSK_WALKUP_${kioskId}`.slice(0, 50);
